import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * <ld-store-availability> — PDP Click & Collect / Delivery widget.
 *
 * Reads the per-location pickup availability rendered server-side (drawer cards
 * carry data-location-id / data-available / data-name / data-address), remembers
 * the shopper's chosen store in localStorage, and shows that store's availability.
 * On variant change it re-fetches the section and morphs in fresh availability
 * (mirrors assets/local-pickup.js), then re-applies the persisted selection.
 *
 * The drawer lists EVERY showroom, not only the ones with online Click & Collect. Liquid cannot
 * enumerate locations, so the full list arrives as the shop metafield `ldd.store_directory` and the
 * per-showroom stock as `ldd.store_stock` on the variant — see the block for how they are joined.
 * A card therefore carries data-click-collect as well as data-available, and a showroom that cannot
 * be collected from online gets a third panel state rather than the misleading red "unavailable".
 *
 * Stock is a BOOLEAN (In stock / Out of stock) — exact quantities and incoming stock stay deferred.
 *
 * CHECKOUT HAND-OFF. A store choice kept only in localStorage never reached checkout, which then
 * picked the pickup location nearest the buyer. So a shopper action (select a store, click a tab)
 * now writes the choice to the cart twice:
 *   1. cart attributes (Ajax API) — `delivery_preference`, `pickup_location_id`,
 *      `pickup_location_name`. They ride onto the order for staff; they select nothing.
 *   2. buyer identity delivery preferences (Storefront API, tokenless, same cart) — this is what
 *      makes checkout open on Pickup at that store. `pickupHandle` takes the NUMERIC location id;
 *      the GID form is accepted by the API but ignored by checkout (verified live 2026-10-27).
 * A shopper who never touches the widget gets no write, so checkout behaves exactly as before.
 */
const STORAGE_KEY = 'ld_selected_store';
const FULFILMENT_KEY = 'ld_fulfilment';

const PREFERENCE_MUTATION = `mutation ldDeliveryPreference($id: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
  cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyerIdentity) { userErrors { message } }
}`;

/** One write chain for the page, so two widgets (PDP + quick-add) cannot interleave their writes. */
let lastSync = Promise.resolve();
/** True once this page has started a write; checkout is held only then. */
let syncStarted = false;

/** @param {string} key */
function readStorage(key) {
  try { return localStorage.getItem(key); } catch (_e) { return null; }
}

/** @param {string} key @param {string} value */
function writeStorage(key, value) {
  try { localStorage.setItem(key, value); } catch (_e) {}
}

/**
 * Write the shopper's choice to the cart. Never throws: a failed write leaves checkout on its own
 * default, which is the behaviour before this existed.
 * @param {string} apiUrl
 * @param {{ method: 'pickup' | 'delivery', storeId: string, storeName: string }} intent
 */
async function pushIntent(apiUrl, intent) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: {
        delivery_preference: intent.method,
        pickup_location_id: intent.storeId,
        pickup_location_name: intent.storeName,
      },
    }),
  });
  if (!response.ok || !apiUrl) return;
  const cart = await response.json();
  if (!cart?.token) return;

  const delivery =
    intent.method === 'pickup'
      ? { deliveryMethod: ['PICK_UP'], pickupHandle: intent.storeId ? [intent.storeId] : [] }
      : { deliveryMethod: ['SHIPPING'], pickupHandle: [] };
  /** @type {Record<string, any>} */
  const buyerIdentity = { preferences: { delivery } };
  if (window.Shopify?.country) buyerIdentity.countryCode = window.Shopify.country;

  await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      query: PREFERENCE_MUTATION,
      variables: { id: `gid://shopify/Cart/${cart.token}`, buyerIdentity },
    }),
  });
}

/**
 * Hold a checkout submit until the last write has landed (at most 2 s), then let it go. Without
 * this, a shopper who selects a store and clicks Checkout at once can beat the write to checkout.
 * @param {SubmitEvent} event
 */
async function holdCheckout(event) {
  const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);
  const form = /** @type {HTMLFormElement} */ (event.target);
  if (!syncStarted || submitter?.name !== 'checkout' || form.dataset.ldSynced === 'true') return;
  event.preventDefault();
  await Promise.race([lastSync, new Promise((resolve) => setTimeout(resolve, 2000))]);
  form.dataset.ldSynced = 'true';
  form.requestSubmit(submitter);
}

/** Great-circle distance in km. Coordinates ride in on the cards, so no geocoding service is needed. */
function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(a));
}

class LdStoreAvailability extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #searchTimer;
  /** @type {Promise<any> | null} */
  #localitiesPromise = null;
  /** @type {Map<string, [string, number, number]> | null} */
  #placeIndex = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #cartSyncTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // Same function reference from every instance, so the browser keeps one copy of it.
    document.addEventListener('submit', holdCheckout, true);
    this.#stampOrder();
    this.#applySelection();
    this.#restoreTab();

    const closestSection = this.closest('.shopify-section, dialog');
    /** @param {CustomEvent} event */
    const variantUpdated = (event) => {
      const detail = event.detail || {};
      if (detail.data && detail.data.newProduct) this.dataset.productUrl = detail.data.newProduct.url;
      const variantId = detail.resource ? detail.resource.id : null;
      if (variantId && String(variantId) !== String(this.dataset.variantId)) {
        this.dataset.variantId = String(variantId);
        this.#fetchAvailability(variantId);
      }
    };
    closestSection?.addEventListener(ThemeEvents.variantUpdate, variantUpdated);
    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('input', this.#onInput);
      document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
      // Only the last widget on the page takes the checkout hold with it.
      if (document.querySelectorAll('ld-store-availability').length === 0) {
        document.removeEventListener('submit', holdCheckout, true);
      }
      clearTimeout(this.#cartSyncTimer);
    };
  }

  /** A collect-only item (oversized) cannot be delivered, so its Delivery tab is information only. */
  get #collectOnly() {
    return this.dataset.collectOnly === 'true';
  }

  /** Open the tab the shopper chose last, on any product page. */
  #restoreTab() {
    if (readStorage(FULFILMENT_KEY) === 'delivery' && !this.#collectOnly) this.#selectTab('delivery');
  }

  /**
   * The shopper's current choice, read from storage and resolved against the rendered cards.
   * @returns {{ method: 'pickup' | 'delivery', storeId: string, storeName: string } | null}
   */
  #intent() {
    const method = readStorage(FULFILMENT_KEY);
    if (method !== 'pickup' && method !== 'delivery') return null;
    if (method === 'delivery') return { method, storeId: '', storeName: '' };

    const storeId = readStorage(STORAGE_KEY);
    const card = storeId
      ? this.#cards().find((c) => c.getAttribute('data-location-id') === storeId)
      : null;
    // A showroom without online Click & Collect is not a checkout pickup location; send the
    // method alone and let checkout pick the store.
    if (!card || card.getAttribute('data-click-collect') === 'false') {
      return { method, storeId: '', storeName: '' };
    }
    return { method, storeId: storeId || '', storeName: card.getAttribute('data-name') || '' };
  }

  /** Queue a write of the current choice to the cart. */
  #syncCart() {
    const intent = this.#intent();
    if (!intent) return;
    syncStarted = true;
    const apiUrl = this.dataset.storefrontApi || '';
    lastSync = lastSync.then(() => pushIntent(apiUrl, intent)).catch(() => {});
  }

  /**
   * A new cart (first add-to-cart, or an expired one) does not carry an earlier choice, so write
   * it again after every cart change. Debounced: quantity steppers fire this in bursts.
   */
  #onCartUpdate = () => {
    clearTimeout(this.#cartSyncTimer);
    this.#cartSyncTimer = setTimeout(() => this.#syncCart(), 300);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const tab = target.closest('[data-tab]');
    if (tab instanceof HTMLElement) {
      this.#selectTab(tab.dataset.tab);
      // On a collect-only item the Delivery tab only explains why it cannot ship; it must not
      // switch the cart to delivery, or checkout would open on a Ship tab with no options.
      if (tab.dataset.tab === 'delivery' && this.#collectOnly) return;
      writeStorage(FULFILMENT_KEY, tab.dataset.tab === 'delivery' ? 'delivery' : 'pickup');
      this.#syncCart();
      return;
    }

    if (target.closest('[data-use-location]')) {
      this.#sortByDistance();
      return;
    }

    const selectBtn = target.closest('[data-select-store]');
    if (selectBtn) {
      const card = selectBtn.closest('[data-location-id]');
      if (card instanceof HTMLElement && card.dataset.locationId) {
        writeStorage(STORAGE_KEY, card.dataset.locationId);
        this.#applySelection();
        this.#closeDrawer();
        // A showroom-only store cannot be a checkout pickup location, so it changes nothing there.
        if (card.dataset.clickCollect !== 'false') {
          writeStorage(FULFILMENT_KEY, 'pickup');
          this.#syncCart();
        }
      }
    }
  };

  /** @param {string | undefined} name */
  #selectTab(name) {
    if (!name) return;
    this.querySelectorAll('[data-tab]').forEach((tab) => {
      const on = tab.getAttribute('data-tab') === name;
      tab.classList.toggle('is-selected', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.getAttribute('data-panel') !== name);
    });
  }

  #closeDrawer() {
    const host = this.querySelector('dialog-component');
    if (host && typeof (/** @type {any} */ (host).closeDialog) === 'function') {
      /** @type {any} */ (host).closeDialog();
    } else {
      /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'))?.close();
    }
  }

  #applySelection() {
    let selectedId = null;
    try { selectedId = localStorage.getItem(STORAGE_KEY); } catch (_e) {}

    const cards = Array.from(this.querySelectorAll('.ld-sa__card[data-location-id]'));
    const card = selectedId
      ? cards.find((c) => c.getAttribute('data-location-id') === selectedId)
      : null;

    const empty = this.querySelector('[data-cc="empty"]');
    const selected = this.querySelector('[data-cc="selected"]');
    if (!empty || !selected) return;

    if (!card) {
      empty.toggleAttribute('hidden', false);
      selected.toggleAttribute('hidden', true);
      return;
    }

    empty.toggleAttribute('hidden', true);
    selected.toggleAttribute('hidden', false);

    const available = card.getAttribute('data-available') === 'true';
    // Absent means a card rendered before this attribute existed; treat it as Click & Collect so an
    // older cached section degrades to the previous two-state behaviour rather than to the new one.
    const clickCollect = card.getAttribute('data-click-collect') !== 'false';
    const nameEl = selected.querySelector('[data-store-name]');
    const addrEl = selected.querySelector('[data-store-address]');
    if (nameEl) nameEl.textContent = card.getAttribute('data-name') || '';
    if (addrEl) addrEl.textContent = card.getAttribute('data-address') || '';

    // Three states, not two. "You cannot collect this online" and "we do not have it" are separate
    // facts, and the old red band said the second when it meant the first.
    const showroomOnly = !clickCollect;
    selected.querySelector('[data-band="available"]')?.toggleAttribute('hidden', showroomOnly || !available);
    selected.querySelector('[data-band="unavailable"]')?.toggleAttribute('hidden', showroomOnly || available);
    const showroomBand = selected.querySelector('[data-band="showroom"]');
    showroomBand?.toggleAttribute('hidden', !showroomOnly);
    showroomBand?.querySelector('[data-showroom-instock]')?.toggleAttribute('hidden', !available);
    showroomBand?.querySelector('[data-showroom-outofstock]')?.toggleAttribute('hidden', available);
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target?.hasAttribute?.('data-store-search')) return;
    const value = /** @type {HTMLInputElement} */ (target).value;
    // Debounced: the gazetteer lookup runs per keystroke and re-sorts the list, and doing that on
    // every character makes the drawer twitch while someone types "palmerston".
    clearTimeout(this.#searchTimer);
    this.#searchTimer = setTimeout(() => this.#search(value), 220);
  };

  /**
   * Resolve what was typed, in the order a shopper means it:
   *   1. nothing            -> restore the list
   *   2. a place or postcode -> sort every store by distance from it
   *   3. anything else      -> fall back to matching the store's own name, city or postcode
   *
   * Step 2 is the one that matters. Someone in Remuera typing "Remuera" used to get "no showrooms
   * match", because no shop is IN Remuera — while Newmarket sits three kilometres away.
   */
  async #search(query) {
    const q = (query || '').trim();
    if (q === '') {
      this.#filter('');
      this.#restoreOrder();
      this.#message('');
      return;
    }

    const place = await this.#lookupPlace(q);
    if (place) {
      this.#filter(''); // a distance sort ranks the whole list; it never hides a store
      this.#sortByPoint(place.lat, place.lng);
      this.#message(`Showrooms nearest ${place.name}`);
      return;
    }

    this.#restoreOrder();
    this.#filter(q);
    this.#message('');
  }

  /** Fetch the gazetteer once, on demand. Failure is not fatal — the substring filter still works. */
  async #localities() {
    if (this.#localitiesPromise) return this.#localitiesPromise;
    const url = this.dataset.localitiesUrl;
    if (!url) return null;
    this.#localitiesPromise = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    return this.#localitiesPromise;
  }

  /** @returns {Promise<{name: string, lat: number, lng: number} | null>} */
  async #lookupPlace(query) {
    const data = await this.#localities();
    if (!data) return null;

    // A four-digit string is a postcode, not a place called "1050".
    if (/^\d{4}$/.test(query)) {
      const hit = data.postcodes?.[query];
      // Name the suburb, not the number: "nearest Remuera" tells a shopper the lookup understood
      // them; "nearest 1050" reads like the search failed and echoed the input back.
      if (hit) return { name: hit[2] || query, lat: hit[0], lng: hit[1] };
    }

    const norm = (s) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '');
    const k = norm(query);
    if (!k) return null;
    if (!this.#placeIndex) {
      this.#placeIndex = new Map();
      for (const p of data.places || []) {
        const nk = norm(p[0]);
        if (!this.#placeIndex.has(nk)) this.#placeIndex.set(nk, p);
      }
    }
    const exact = this.#placeIndex.get(k);
    if (exact) return { name: exact[0], lat: exact[1], lng: exact[2] };

    // Prefix match, shortest name wins — "palmer" should land on Palmerston North, not on a hamlet
    // that merely starts the same way and happens to sort first.
    let best = null;
    for (const [nk, p] of this.#placeIndex) {
      if (nk.startsWith(k) && (best === null || p[0].length < best[0].length)) best = p;
    }
    return best ? { name: best[0], lat: best[1], lng: best[2] } : null;
  }

  /** All rendered cards, across both groups. */
  #cards() {
    return Array.from(this.querySelectorAll('.ld-sa__card[data-location-id]'));
  }

  /**
   * Plain substring match over the name, city and postcode baked into `data-search`.
   *
   * Deliberately NOT a postcode-to-distance lookup: that needs either a geocoding API or a table of
   * every NZ postcode, and neither earns its place next to a twenty-three item list. "Use my
   * location" covers the real "what is near me" question exactly, with no external dependency.
   */
  #filter(query) {
    const q = (query || '').trim().toLowerCase();
    let shown = 0;
    for (const card of this.#cards()) {
      const hit = q === '' || (card.getAttribute('data-search') || '').includes(q);
      card.toggleAttribute('hidden', !hit);
      if (hit) shown++;
    }
    // Hide a group heading whose entire list was filtered out, or it labels nothing.
    for (const list of this.querySelectorAll('.ld-sa__store-list')) {
      const anyVisible = Array.from(list.children).some((li) => !li.hasAttribute('hidden'));
      list.toggleAttribute('hidden', !anyVisible);
      const heading = list.previousElementSibling;
      if (heading?.classList.contains('ld-sa__group')) heading.toggleAttribute('hidden', !anyVisible);
    }
    this.querySelector('[data-no-results]')?.toggleAttribute('hidden', shown > 0);
  }

  #message(text) {
    const el = this.querySelector('[data-search-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.toggleAttribute('hidden', !text);
  }

  /**
   * Sort each group by distance from the shopper.
   *
   * Sorts WITHIN the two groups rather than across them: a shopper who can order online and collect
   * should still see those shops first, even if a showroom they cannot order from is closer.
   */
  #sortByPoint(latitude, longitude) {
    let sorted = 0;
    for (const list of this.querySelectorAll('.ld-sa__store-list')) {
      const cards = Array.from(list.children);
      for (const card of cards) {
        const lat = parseFloat(card.getAttribute('data-lat') || '');
        const lng = parseFloat(card.getAttribute('data-lng') || '');
        const label = card.querySelector('[data-distance]');
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const km = haversineKm(latitude, longitude, lat, lng);
          card.dataset.km = String(km);
          sorted++;
          if (label) {
            label.textContent = km < 10 ? `${km.toFixed(1)} km away` : `${Math.round(km)} km away`;
            label.toggleAttribute('hidden', false);
          }
        } else {
          // No coordinate — park it at the end rather than at the top, where Infinity would sort.
          card.dataset.km = String(Number.MAX_SAFE_INTEGER);
          label?.toggleAttribute('hidden', true);
        }
      }
      cards
        .sort((a, b) => Number(a.dataset.km) - Number(b.dataset.km))
        .forEach((card) => list.appendChild(card));
    }
    return sorted;
  }

  /** Put the list back in its server-rendered order and drop the distance labels. */
  #restoreOrder() {
    for (const list of this.querySelectorAll('.ld-sa__store-list')) {
      Array.from(list.children)
        .sort((a, b) => Number(a.dataset.order || 0) - Number(b.dataset.order || 0))
        .forEach((card) => {
          list.appendChild(card);
          card.querySelector('[data-distance]')?.toggleAttribute('hidden', true);
        });
    }
  }

  /** Remember the rendered order so a distance sort can be undone. */
  #stampOrder() {
    for (const list of this.querySelectorAll('.ld-sa__store-list')) {
      Array.from(list.children).forEach((card, i) => {
        card.dataset.order = String(i);
      });
    }
  }

  #sortByDistance() {
    if (!navigator.geolocation) {
      this.#message('Location is not available in this browser.');
      return;
    }
    this.#message('Finding your location…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const sorted = this.#sortByPoint(pos.coords.latitude, pos.coords.longitude);
        this.#message(sorted > 0 ? 'Showrooms nearest you' : 'No showrooms have a location on file.');
      },
      () => this.#message('We could not get your location. Search by town or postcode instead.'),
      { timeout: 8000, maximumAge: 300000 },
    );
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /** @param {string} variantId */
  #fetchAvailability(variantId) {
    if (!variantId || !this.dataset.productUrl) return;
    const abortController = this.#createAbortController();

    fetch(`${this.dataset.productUrl}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh =
          doc.querySelector(`ld-store-availability[data-variant-id="${variantId}"]`) ||
          doc.querySelector('ld-store-availability');
        if (!fresh) return;
        const activeTab = this.querySelector('.ld-sa__tab.is-selected')?.getAttribute('data-tab') || 'cc';
        morph(this, fresh);
        this.#selectTab(activeTab);
        this.#stampOrder();
        this.#applySelection();
      })
      .catch(() => {});
  }
}

if (!customElements.get('ld-store-availability')) {
  customElements.define('ld-store-availability', LdStoreAvailability);
}
